import axios from 'axios'
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import toast from 'react-hot-toast'
import { apiMonitoringMiddleware } from '../utils/monitoring'

// Extend AxiosRequestConfig to include monitoring metadata
interface MonitoringRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: number;
    monitoring: {
      onSuccess: (status: number) => void;
      onError: (status: number) => void;
    };
  };
}

// Track if we're currently refreshing to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

// Create axios instance with production-ready configuration
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080',
  timeout: 30000, // Increased timeout for production
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // Set to true if using cookies
})

// Request interceptor to add auth token and monitoring
export function authRequestInterceptor(config: InternalAxiosRequestConfig) {
  const token = localStorage.getItem('authToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  
  // Add monitoring metadata
  const monitoringConfig = config as MonitoringRequestConfig;
  monitoringConfig.metadata = {
    startTime: performance.now(),
    monitoring: apiMonitoringMiddleware(config.url || '', config.method?.toUpperCase() || 'GET')
  }
  
  return config
}

// Enhanced response interceptor with token refresh
export async function errorResponseInterceptor(error: any) {
  const originalRequest = error.config;

  // Suppress toasts for background/automatic API calls
  const isBackgroundCall = 
    error?.config?.url?.includes('/analytics') || 
    error?.config?.url?.includes('/metrics') ||
    error?.config?.url?.includes('/unread-count') ||
    (error?.config?.url?.includes('/messages') && error?.config?.method === 'get');
    
  if (isBackgroundCall) {
    return Promise.reject(error);
  }

  if (error.response) {
    const status = error.response.status;
    const message = error.response.data?.message || 'An error occurred';
    
    // Handle 401 with automatic token refresh
    if (status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If we're already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      
      if (refreshToken) {
        try {
          // Attempt token refresh
          const refreshResponse = await axios.post(`${import.meta.env.VITE_AUTH_SERVICE_URL || 'http://localhost:8081'}/refresh`, {
            refreshToken
          });
          
          const { token: newToken, refreshToken: newRefreshToken } = refreshResponse.data;
          
          // Update stored tokens
          localStorage.setItem('authToken', newToken);
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
          }
          
          // Update default authorization header
          api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          
          processQueue(null, newToken);
          isRefreshing = false;
          
          // Retry original request
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          isRefreshing = false;
          
          // Refresh failed, redirect to login
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          
          // Only redirect if not already on login page
          if (!window.location.pathname.includes('/login')) {
            toast.error('Session expired. Please log in again.');
            window.location.href = '/login';
          }
          
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, redirect to login
        processQueue(error, null);
        isRefreshing = false;
        
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        
        if (!window.location.pathname.includes('/login')) {
          toast.error('Session expired. Please log in again.');
          window.location.href = '/login';
        }
        
        return Promise.reject(error);
      }
    }

    // Handle other HTTP errors
    switch (status) {
      case 403:
        toast.error('Access denied');
        break;
      case 404:
        toast.error('Resource not found');
        break;
      case 422:
        toast.error('Validation error');
        break;
      case 429:
        toast.error('Too many requests. Please try again later.');
        break;
      case 500:
        toast.error('Server error. Please try again later.');
        break;
      case 502:
      case 503:
      case 504:
        toast.error('Service temporarily unavailable. Please try again.');
        break;
      default:
        toast.error(message);
    }
  } else if (error.request) {
    // Network error
    toast.error('Network error. Please check your connection.');
  } else {
    // Other error
    toast.error('An unexpected error occurred.');
  }
  
  return Promise.reject(error);
}

// Success response interceptor with monitoring
function successResponseInterceptor(response: AxiosResponse) {
  // Track successful API calls
  const monitoringConfig = response.config as MonitoringRequestConfig;
  if (monitoringConfig.metadata?.monitoring) {
    monitoringConfig.metadata.monitoring.onSuccess(response.status);
  }
  return response;
}

// Setup interceptors
api.interceptors.request.use(authRequestInterceptor, (error) => Promise.reject(error));
api.interceptors.response.use(successResponseInterceptor, errorResponseInterceptor);

export default api; 